import { useEffect } from 'react';
import { Link } from 'react-router-dom';

function LegalTable({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 my-4">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {headers.map((h) => (
              <th
                key={h}
                className="px-4 py-3 text-left font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-3 text-gray-700 align-top">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Section({ number, title, children }: { number: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-base font-bold text-gray-900 mb-3">
        {number} ({title})
      </h2>
      {children}
    </section>
  );
}

export default function PrivacyPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 sm:px-6 h-14 flex items-center">
        <Link
          to="/"
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-600 transition-colors font-medium"
        >
          ← MaioS 홈으로
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="text-2xl font-bold text-gray-900">MaioS 개인정보처리방침</h1>
        <p className="mt-1 text-sm text-gray-500">시행일: 2026년 7월 3일</p>

        <p className="mt-6 text-sm text-gray-700 leading-relaxed">
          김대웅(이하 "운영자")은 MaioS 서비스(이하 "서비스") 이용자의 개인정보를 소중히 여기며,
          「개인정보 보호법」에 따라 이용자의 개인정보를 안전하게 보호합니다.
        </p>

        {/* 제1조 */}
        <Section number="제1조" title="수집하는 개인정보 항목 및 수집 방법">
          <p className="text-sm font-semibold text-gray-800 mb-2">① 수집 항목</p>
          <LegalTable
            headers={['구분', '항목']}
            rows={[
              ['필수', '이메일 주소, 이름(닉네임), 비밀번호(암호화 저장)'],
              ['자동 수집', '접속 IP, 브라우저 종류, 서비스 이용 기록, 오류 로그'],
            ]}
          />
          <p className="text-sm font-semibold text-gray-800 mt-4 mb-2">② 수집 방법</p>
          <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
            <li>회원가입 시 이용자가 직접 입력</li>
            <li>서비스 이용 과정에서 자동 생성 및 수집</li>
          </ul>
        </Section>

        {/* 제2조 */}
        <Section number="제2조" title="개인정보의 수집 및 이용 목적">
          <LegalTable
            headers={['목적', '상세 내용']}
            rows={[
              ['회원 가입 및 관리', '본인 확인, 이메일 인증, 서비스 이용계약 체결·유지·해지'],
              ['서비스 제공', '프로젝트 관리, 팀 협업 기능, 초대 이메일 발송'],
              ['서비스 개선', '오류 탐지 및 버그 수정'],
              ['공지 및 안내', '서비스 정책 변경 안내, 베타 종료 후 플랜 전환 안내'],
            ]}
          />
        </Section>

        {/* 제3조 */}
        <Section number="제3조" title="개인정보의 보유 및 이용 기간">
          <ul className="text-sm text-gray-700 list-disc list-inside space-y-1 mb-4">
            <li>회원 탈퇴 시까지 보유 후 즉시 파기합니다.</li>
            <li>단, 관계 법령에서 보존을 규정한 경우에는 해당 기간 동안 보유합니다.</li>
          </ul>
          <LegalTable
            headers={['관련 법령', '보유 항목', '보유 기간']}
            rows={[
              ['전자상거래 등에서의 소비자보호에 관한 법률', '계약·청약 철회 기록', '5년'],
              ['통신비밀보호법', '로그인 기록', '3개월'],
            ]}
          />
        </Section>

        {/* 제4조 */}
        <Section number="제4조" title="개인정보의 제3자 제공">
          <p className="text-sm text-gray-700 leading-relaxed">
            운영자는 원칙적으로 이용자의 개인정보를 제3자에게 제공하지 않습니다. 다만, 이용자의 별도
            동의가 있거나 법령에 의한 경우는 예외입니다.
          </p>
        </Section>

        {/* 제5조 */}
        <Section number="제5조" title="개인정보 처리 위탁">
          <p className="text-sm text-gray-700 mb-3 leading-relaxed">
            운영자는 원활한 서비스 제공을 위해 아래와 같이 개인정보 처리 업무를 위탁하고 있습니다.
          </p>
          <LegalTable
            headers={['수탁자', '위탁 업무', '보유 및 이용 기간']}
            rows={[
              ['Vultr Inc. (한국 서울 리전)', '서버 운영 및 데이터 저장', '회원 탈퇴 시까지'],
              ['Resend Inc. (미국)', '인증 및 서비스 이메일 발송', '발송 완료 후 즉시 파기'],
              ['Sentry Inc. (미국)', '서비스 오류 모니터링', '수집 후 30일'],
            ]}
          />
        </Section>

        {/* 제6조 */}
        <Section number="제6조" title="개인정보의 국외 이전">
          <p className="text-sm text-gray-700 mb-4 leading-relaxed">
            운영자는 서비스 운영을 위해 아래와 같이 개인정보를 국외로 이전합니다.
          </p>

          <p className="text-sm font-semibold text-gray-800 mb-2">① Resend Inc.</p>
          <LegalTable
            headers={['항목', '내용']}
            rows={[
              ['이전받는 자', 'Resend Inc.'],
              ['이전 국가', '미국'],
              ['이전 항목', '이메일 주소'],
              ['이전 목적', '인증 이메일 및 서비스 알림 발송'],
              ['보유 기간', '발송 완료 후 즉시 파기'],
            ]}
          />

          <p className="text-sm font-semibold text-gray-800 mt-5 mb-2">② Sentry Inc.</p>
          <LegalTable
            headers={['항목', '내용']}
            rows={[
              ['이전받는 자', 'Sentry Inc.'],
              ['이전 국가', '미국'],
              ['이전 항목', '접속 IP, 브라우저 정보, 오류 발생 시 관련 로그'],
              ['이전 목적', '서비스 오류 탐지 및 개선'],
              ['보유 기간', '수집 후 30일'],
            ]}
          />
        </Section>

        {/* 제7조 */}
        <Section number="제7조" title="개인정보의 파기">
          <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
            <li>
              <span className="font-semibold">파기 절차</span>: 수집 및 이용 목적이 달성되면 즉시
              파기합니다.
            </li>
            <li>
              <span className="font-semibold">파기 방법</span>: 전자적 파일은 복구 불가능한 기술적
              방법으로 삭제합니다.
            </li>
          </ul>
        </Section>

        {/* 제8조 */}
        <Section number="제8조" title="이용자의 권리와 행사 방법">
          <p className="text-sm text-gray-700 mb-3">이용자는 언제든지 다음 권리를 행사할 수 있습니다.</p>
          <LegalTable
            headers={['권리', '행사 방법']}
            rows={[
              ['개인정보 열람·수정', '서비스 내 [계정 설정] 메뉴'],
              [
                '회원 탈퇴(삭제)',
                '서비스 내 [계정 설정 → 계정 삭제] 또는 책임자 이메일 요청',
              ],
            ]}
          />
        </Section>

        {/* 제9조 */}
        <Section number="제9조" title="쿠키 및 자동 수집 장치">
          <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
            <li>
              운영자는 로그인 상태 유지 및 서비스 개선을 위해 쿠키(Cookie)를 사용합니다.
            </li>
            <li>
              이용자는 브라우저 설정을 통해 쿠키 저장을 거부할 수 있으나, 이 경우 로그인 등 일부 서비스
              이용이 제한될 수 있습니다.
            </li>
          </ul>
        </Section>

        {/* 제10조 */}
        <Section number="제10조" title="개인정보 보호 책임자">
          <p className="text-sm text-gray-700 mb-2">
            개인정보 관련 문의, 불만 처리, 피해 구제 등은 아래로 연락주시기 바랍니다.
          </p>
          <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
            <li>
              <span className="font-semibold">이름</span>: 김대웅
            </li>
            <li>
              <span className="font-semibold">이메일</span>:{' '}
              <a
                href="mailto:privacy@maios.kr"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                privacy@maios.kr
              </a>
            </li>
          </ul>
        </Section>

        {/* 제11조 */}
        <Section number="제11조" title="처리방침의 변경">
          <p className="text-sm text-gray-700 leading-relaxed">
            이 개인정보처리방침은 <strong>2026년 7월 3일</strong>부터 시행됩니다. 법령·정책 또는 보안
            기술의 변경에 따라 내용이 변경될 경우, 시행 7일 전에 서비스 내 공지 또는 이메일을 통해
            안내합니다.
          </p>
        </Section>

        <div className="mt-12 pt-6 border-t border-gray-200 text-xs text-gray-400 text-center">
          © 2026 MaioS. All rights reserved.
        </div>
      </main>
    </div>
  );
}
